import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Gift, ArrowRight, Loader2 } from "lucide-react";

type Stats = {
  code: string | null;
  qualified_count: number;
  next_milestone: number;
  credits_approved_months: number;
};

export function ReferralCard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      await supabase.rpc("get_or_create_referral_code");
      const { data } = await supabase.rpc("get_my_referral_stats");
      if (data && Array.isArray(data) && data[0]) setStats(data[0] as Stats);
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <Card className="bg-card/60 border-border/50 p-5 flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
      </Card>
    );
  }

  const q = stats?.qualified_count ?? 0;
  const next = stats?.next_milestone ?? 7;
  const pct = Math.min(100, Math.round((q / next) * 100));

  return (
    <Card className="bg-card/60 border-border/50 p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Gift className="w-5 h-5 text-accent" />
          <h3 className="font-semibold text-foreground">Referral program</h3>
        </div>
        {(stats?.credits_approved_months ?? 0) > 0 && (
          <span className="text-xs px-2 py-0.5 rounded-lg bg-accent/15 text-accent">
            {stats?.credits_approved_months} mo credit
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Refer 7 friends for 1 month free, 30 for 3 months, 50 for 6 months.
      </p>
      <div className="mb-4">
        <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
          <span>Qualified referrals</span>
          <span>{q} / {next}</span>
        </div>
        <div className="h-1.5 bg-border/30 rounded-full overflow-hidden">
          <div className="h-full bg-accent" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <Button asChild variant="secondary" size="sm" className="w-full gap-2">
        <Link to="/ai-investing-club/refer">
          Get your link <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </Button>
    </Card>
  );
}
